import { computed } from 'vue';
import { useAuthStore } from '../stores/auth';
import { useCompanyStore } from '../stores/company';
import { useModuleVisibility } from './useModuleVisibility';
import { useViewerConfig, viewerModuleConfig, viewerModuleFor } from './useViewerConfig';
import { useSessionCustomRole, ensureSessionCustomRoleLoaded } from './useSessionCustomRole';
import { customRoleHasAction } from '../domain/types/CustomRole';
import {
  asCompanyRoles,
  customRoleIdFor,
  regularHasAction,
  resolveUserRole,
  type ModuleAction,
  type UserRole
} from '../domain/types/Role';

/**
 * Resuelve el acceso efectivo a un módulo: flags de la compañía ∧ permisos del
 * usuario. Los flags de la compañía mandan, así que un admin tampoco accede a
 * un módulo que su compañía tiene apagado.
 *
 * El plan se valida aparte, con isModuleAvailableForPlan, porque depende de la
 * suscripción y no del usuario.
 */
export function usePermissions() {
  const auth = useAuthStore();
  const companyStore = useCompanyStore();
  const { isModuleVisible } = useModuleVisibility();
  const viewerConfig = useViewerConfig();

  const userRole = computed<UserRole | null>(() =>
    resolveUserRole(auth.user?.role, auth.user?.clientType)
  );

  // Solo carga para viewers (early-return interno) y cachea por compañía.
  void viewerConfig.loadViewerConfig();

  const sessionRole = useSessionCustomRole();
  void ensureSessionCustomRoleLoaded();

  const hasPermission = (module: string, action: ModuleAction) => {
    const role = userRole.value;
    if (!role) return false;

    if (!isModuleVisible(module)) return false;

    if (role === 'superadmin' || role === 'admin') return true;
    if (role === 'viewer') {
      // El viewer solo alcanza los módulos que tienen configuración de solo
      // vista propia: embarques y órdenes de compra. Todo lo demás queda fuera
      // aunque la compañía lo tenga habilitado — un invitado de solo vista no
      // debe ver el buzón, los reportes ni la configuración de la compañía.
      const viewerModule = viewerModuleFor(module);
      if (!viewerModule) return false;

      if (action === 'read') {
        // Mientras la configuración no ha llegado se falla abierto: el guard del
        // router corre antes del primer getDoc y expulsaría al viewer de un
        // módulo al que sí tiene acceso. El cierre lo hace cada página, que
        // renderiza cuando la configuración ya está.
        if (!viewerConfig.isReady.value) return true;
        return viewerModuleConfig(viewerModule).enabled;
      }
      if (action === 'export') return viewerModuleConfig(viewerModule).canExportData;
      return false;
    }

    const companyId = companyStore.selectedCompanyId;
    if (!companyId) return false;

    const customRoleId = customRoleIdFor(asCompanyRoles(auth.user?.companyRoles), companyId, role);
    if (customRoleId) {
      // Igual que el viewer: el guard espera la carga; mientras tanto solo se falla abierto en lectura.
      if (!sessionRole.isReady.value) return action === 'read';
      return customRoleHasAction(sessionRole.role.value, module, action);
    }
    return regularHasAction(auth.user?.permissions, companyId, module, action);
  };

  const canRead = (module: string) => hasPermission(module, 'read');
  const canCreate = (module: string) => hasPermission(module, 'create');
  const canUpdate = (module: string) => hasPermission(module, 'update');
  const canDelete = (module: string) => hasPermission(module, 'delete');
  const canExport = (module: string) => hasPermission(module, 'export');
  const canApprove = (module: string) => hasPermission(module, 'approve');

  const isSuperAdmin = () => userRole.value === 'superadmin';
  const isAdmin = () => userRole.value === 'admin';
  const isViewer = () => userRole.value === 'viewer';
  const isAdminOrSuperAdmin = () => isAdmin() || isSuperAdmin();

  return {
    userRole,
    // Falso mientras un viewer espera su configuración o un usuario con rol personalizado su rol.
    // El menú lo usa para no pintar módulos que luego desaparecen.
    viewerConfigReady: computed(() => viewerConfig.isReady.value && sessionRole.isReady.value),
    hasPermission,
    canRead,
    canCreate,
    canUpdate,
    canDelete,
    canExport,
    canApprove,
    isSuperAdmin,
    isAdmin,
    isViewer,
    isAdminOrSuperAdmin
  };
}
